import {
  ITile,
  Maybe,
  MoveMoneyPageSectionTile,
  MoveMoneyVxlTile,
  ProductOfferSingleCardTile,
  RouteNavigationAction,
  Tag,
  TileStatus,
  TileType,
  Typenames,
  VxlAction,
  VxlActionTypenames,
  VxlIconType,
  VxlIllustrationType,
  VxlListItem,
  VxlListItemLeadingAccessoryResult,
  VxlSectionDivider,
} from '@varo/graphql-schema-types';

// Typename constants for leading accessory discrimination.
const VXL_ICON_TYPE_TYPENAME = 'VXLIconType';
const VXL_ILLUSTRATION_TYPE_TYPENAME = 'VXLIllustrationType';

/**
 * Type guard: narrows a leading accessory to VXLIconType.
 * Relies on __typename discrimination and avoids unsafe casts.
 */
export const isVxlIconType = (
  accessory: VxlListItemLeadingAccessoryResult | undefined,
): accessory is VxlIconType => {
  return !!accessory && accessory.__typename === VXL_ICON_TYPE_TYPENAME;
};

/**
 * Type guard: narrows a leading accessory to VXLIllustrationType.
 * Relies on __typename discrimination and avoids unsafe casts.
 */
export const isVxlIllustrationType = (
  accessory: VxlListItemLeadingAccessoryResult | undefined,
): accessory is VxlIllustrationType => {
  return !!accessory && accessory.__typename === VXL_ILLUSTRATION_TYPE_TYPENAME;
};

export const isVxlSectionDivider = (
  content: unknown,
): content is VxlSectionDivider =>
  typeof content === 'object' &&
  content !== null &&
  '__typename' in content &&
  content.__typename === Typenames.VxlSectionDivider;

/**
 * Type guard: narrows an ITile to MoveMoneyPageSectionTile.
 * Checks both __typename and tileType for compatibility with mock data.
 */
export const isMoveMoneyPageSectionTile = (
  tile: ITile,
): tile is MoveMoneyPageSectionTile => {
  return (
    tile.tileType === TileType.MoveMoneyPageSectionTile ||
    ('__typename' in tile &&
      tile.__typename === Typenames.MoveMoneyPageSectionTile)
  );
};

/**
 * Type guard: narrows MoveMoneyVxlComponentResult to VxlListItem.
 * Uses exclusion: if it's not a VxlSectionDivider, it must be a VxlListItem.
 */
export const isVxlListItem = (
  content: VxlListItem | VxlSectionDivider,
): content is VxlListItem => {
  return !isVxlSectionDivider(content);
};

/**
 * Type guard: narrows VxlAction to RouteNavigationAction.
 */
export const isRouteNavigationAction = (
  action: Maybe<VxlAction> | undefined,
): action is RouteNavigationAction => {
  return (
    !!action &&
    '__typename' in action &&
    action.__typename === VxlActionTypenames.RouteNavigationAction
  );
};

/**
 * Type guard: narrows VxlListItemTrailingAccessoryResult to Tag.
 */
export const isTag = (
  accessory: Tag | { __typename?: string } | null | undefined,
): accessory is Tag => {
  return (
    !!accessory &&
    '__typename' in accessory &&
    accessory.__typename === Typenames.Tag
  );
};

/**
 * Type guard: narrows ITile to ProductOfferSingleCardTile.
 */
export const isProductOfferSingleCardTile = (
  tile: ITile | null | undefined,
): tile is ProductOfferSingleCardTile => {
  return (
    !!tile &&
    '__typename' in tile &&
    tile.__typename === Typenames.ProductOfferSingleCardTile
  );
};

/**
 * Type guard: narrows a MoveMoneyVxlTile or ProductOfferSingleCardTile
 * to a loaded MoveMoneyVxlTile.
 */
export const isLoadedMoveMoneyVxlTile = (
  tile: MoveMoneyVxlTile | ProductOfferSingleCardTile,
): tile is MoveMoneyVxlTile => {
  return (
    !!tile &&
    tile.tileType === TileType.MoveMoneyVxlTile &&
    tile.status === TileStatus.Loaded
  );
};
